import { Badge, HStack, Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCreditFacilityRetrieveSuspense } from "gen/endpoints/credit-facility/credit-facility";
import {
  getCreditFacilityBorrowingBaseGroupListQueryKey,
  useCreditFacilityBorrowingBaseGroupListSuspense,
} from "gen/endpoints/credit-facility-borrowing-base-group/credit-facility-borrowing-base-group";
import {
  useCreditFacilityBorrowingBaseInvestorEntitiesTableDataRetrieveSuspense,
  useCreditFacilityBorrowingBaseToInvestorEntitiesUpdate,
} from "gen/endpoints/credit-facility-borrowing-base-investors/credit-facility-borrowing-base-investors";
import { BooleanTableCellValue } from "gen/model/booleanTableCellValue";
import { PercentTableCellValue } from "gen/model/percentTableCellValue";
import { TextTableCellValue } from "gen/model/textTableCellValue";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBadges } from "shared/components/table/box/TableBoxBadges";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { defaultDataTableRenderCellValue } from "shared/components/table/data/DataTableCellValueRenderer";
import { RenderCellValueFn } from "shared/components/table/data/DataTableTypes";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import {
  formatPercentage,
  formatPercentageForTable,
} from "shared/utils/formattingUtils";

export const RateFormulaCellComponent: React.FC<{
  rate: string;
}> = ({ rate }) => (
  <HStack justifyContent="space-between">
    <Text>x</Text>
    <Text flexShrink={0}>
      {Number(rate) === 0
        ? formatPercentage("0")
        : formatPercentageForTable(rate)}
    </Text>
    <Text>=</Text>
  </HStack>
);

export const AdvanceRateGroupCell: React.FC<{
  groupName: string;
  currentGroupName: string | null;
  currentRowId: string | undefined | null;
  selectedIds: string[];
}> = ({ groupName, currentGroupName, selectedIds, currentRowId }) => {
  const { t } = useTranslation(["creditFacility"]);
  if (!currentRowId) {
    return currentGroupName;
  }
  if (selectedIds.includes(currentRowId) && !currentGroupName) {
    return groupName;
  }
  if (currentGroupName == groupName && !selectedIds.includes(currentRowId)) {
    return null;
  }
  if (currentGroupName !== groupName && selectedIds.includes(currentRowId)) {
    return (
      <HStack>
        <Text>{groupName}</Text>
        <Badge colorScheme="warning">
          {t("creditFacility:borrowingBase.update.investors.moved")}
        </Badge>
      </HStack>
    );
  }
  return currentGroupName;
};

export const BorrowingBaseInvestorPage: React.FC = () => {
  const { t } = useTranslation(["creditFacility"]);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { fundFamilyId, creditFacilityId, borrowingBaseGroupId } =
    useRequiredStringParams();

  const { data: creditFacility } = useCreditFacilityRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
  );
  const { data: borrowingBaseGroupData } =
    useCreditFacilityBorrowingBaseGroupListSuspense(
      fundFamilyId,
      creditFacilityId,
      {
        version_date: creditFacility.inception_date,
      },
      {
        query: {
          refetchOnMount: "always",
        },
      },
    );
  const groupName = borrowingBaseGroupData.find(
    (group) => group.id === borrowingBaseGroupId,
  )?.name;
  const form = useForm();

  const { mutateAsync: updateGroupWithInvestmentEntities } =
    useCreditFacilityBorrowingBaseToInvestorEntitiesUpdate();

  const { data: tableData } =
    useCreditFacilityBorrowingBaseInvestorEntitiesTableDataRetrieveSuspense(
      fundFamilyId,
      creditFacilityId,
      borrowingBaseGroupId,
      {
        version_date: creditFacility.inception_date,
      },
    );

  // this is all of the entities and their selected state
  const [rowSelectionState] = useState(
    tableData.rows.reduce<{
      [id: string]: boolean;
    }>(
      (state, row) => ({
        ...state,
        [row.id]: (row.cells.is_selected.value as BooleanTableCellValue).value,
      }),
      {},
    ),
  );
  // this is only the selected entities
  const [selectedState, setSelectedState] = useState(
    Object.keys(rowSelectionState).reduce(
      (selectedState, id) => ({
        ...selectedState,
        ...(rowSelectionState[id] ? { [id]: true } : {}),
      }),
      {},
    ),
  );

  const renderCellValue = useCallback<RenderCellValueFn>(
    (props, fieldDisabled, params) => {
      const { columnContext, cell, row } = props;
      if (columnContext.columnId == "rate") {
        return (
          <RateFormulaCellComponent
            rate={(cell.value as PercentTableCellValue).value}
          />
        );
      }
      if (columnContext.columnId == "advance_rate_group") {
        return (
          <AdvanceRateGroupCell
            currentGroupName={(cell.value as TextTableCellValue).text}
            groupName={groupName!}
            currentRowId={row.id}
            selectedIds={Object.keys(selectedState)}
          />
        );
      }
      return defaultDataTableRenderCellValue(props, fieldDisabled, params);
    },
    [groupName, selectedState],
  );

  const creditFacilityRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .creditFacilities({})
    .creditFacility({ creditFacilityId });

  const goBack = () =>
    navigate(creditFacilityRoute.setup({}).borrowingBase({}).$);

  return (
    <PageContent>
      <PageHeader
        title={t(
          "creditFacility:borrowingBase.update.investors.selectIncludedInvestorEntities",
          { groupName },
        )}
        helperText={t("creditFacility:borrowingBase.update.investors.info", {
          groupName,
        })}
      />
      <PageBody>
        <Form
          form={form}
          onSubmit={async () =>
            await updateGroupWithInvestmentEntities({
              fundFamilyId,
              creditFacilityId,
              borrowingBaseGroupId,
              data: {
                investor_entity_ids: Object.keys(selectedState),
              },
              params: {
                version_date: creditFacility.inception_date,
              },
            })
          }
          onSuccess={async () => {
            await queryClient.invalidateQueries({
              queryKey: getCreditFacilityBorrowingBaseGroupListQueryKey(
                fundFamilyId,
                creditFacilityId,
              ),
            });
            goBack();
          }}
        >
          <TableBox isFullWidth={true} isFullHeight={true}>
            <TableBoxHeader>
              <TableBoxTitle>
                {t("creditFacility:borrowingBase.update.investorEntities")}
              </TableBoxTitle>
              <TableBoxBadges>
                <Badge>
                  {t(
                    "creditFacility:borrowingBase.update.investors.investorEntitiesSelected",
                    {
                      count: Object.keys(selectedState).length,
                    },
                  )}
                </Badge>
              </TableBoxBadges>
              <DataTableBoxActions />
            </TableBoxHeader>
            <TableBoxBody>
              <DataTable
                data={tableData}
                rowSelection={rowSelectionState}
                onRowSelectionChange={setSelectedState}
                renderCellValue={renderCellValue}
              />
            </TableBoxBody>
          </TableBox>
          <FormFooter>
            <FormFooterActions>
              <ButtonOrder
                dismiss={<FormCancelButton onClick={goBack} />}
                confirm={
                  <FormSubmitButton>
                    {t(
                      "creditFacility:borrowingBase.update.investors.saveSelection",
                    )}
                  </FormSubmitButton>
                }
              />
            </FormFooterActions>
          </FormFooter>
        </Form>
      </PageBody>
    </PageContent>
  );
};
